import { Member } from '@aibees/shared/definition';
import { createPaginationResponse } from '@aibees/shared/http';
import { useMemo } from 'react';
import { useBrowseWorkspaceMembers } from './use-browse-workspace-members';

const STATUS_PRIORITY = ['ACTIVE', 'PENDING', 'INACTIVE', 'DELETED'];

/**
 * Browse hook for team members that satisfies the BrowseApi<D> contract.
 * Handles client-side sorting and pagination so the config can hold
 * a stable function reference — same pattern as useBrowsePresetApi / useBrowseLabel.
 *
 * The advance table only destructures { data, isLoading, refetch, isFetching }
 * from the result, so we cast to BrowseApi to satisfy the config type.
 */
export function useBrowseTeamMembersApi(params: Record<string, any>) {
  const { browseApi } = useBrowseWorkspaceMembers();
  const allData = browseApi.data ?? [];

  const sort = params.sort as string | undefined;
  const page = Number(params.page) || 0;
  const size = Number(params.size) || 5;

  const data = useMemo(() => {
    let items = allData;

    if (sort) {
      const [, direction] = sort.split(':');
      items = [...items].sort((a, b) => {
        if (a === undefined || b === undefined) return 1;
        const indexA = STATUS_PRIORITY.indexOf(a.status);
        const indexB = STATUS_PRIORITY.indexOf(b.status);
        return direction === 'asc' ? indexA - indexB : indexB - indexA;
      });
    }

    const from = page * size;
    return createPaginationResponse<Member>({
      content: items.slice(from, from + size),
      number: page,
      size,
      totalElements: items.length,
    });
  }, [allData, sort, page, size]);

  return {
    data,
    isLoading: false,
    isFetching: false,
    refetch: (() => {}) as any,
  };
}
